import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  MessageSquare,
  MailPlus,
  BellOff,
  Phone,
  Loader2,
  Info,
  Settings,
  AlertCircle,
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { firmApi, twilioApi } from "../../services/api";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type ChannelOption = "email" | "sms" | "both" | "none";
type PreferredChannel = "email" | "sms" | "phone" | "whatsapp";

interface CommunicationPreferences {
  invoiceNotifications: ChannelOption;
  paymentReceipts: ChannelOption;
  appointmentReminders: ChannelOption;
  matterUpdates: ChannelOption;
  documentRequests: ChannelOption;
  generalCommunications: boolean;
  preferredChannel: PreferredChannel;
}

const DEFAULT_PREFS: CommunicationPreferences = {
  invoiceNotifications: "email",
  paymentReceipts: "email",
  appointmentReminders: "both",
  matterUpdates: "email",
  documentRequests: "email",
  generalCommunications: true,
  preferredChannel: "email",
};

const CHANNEL_OPTIONS: { value: ChannelOption; label: string; icon: React.FC<any> }[] = [
  { value: "email", label: "Email", icon: Mail },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "both", label: "Both", icon: MailPlus },
  { value: "none", label: "None", icon: BellOff },
];

const PREFERRED_CHANNELS: { value: PreferredChannel; label: string; icon: React.FC<any> }[] = [
  { value: "email", label: "Email", icon: Mail },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "phone", label: "Phone", icon: Phone },
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare },
];

const NOTIFICATION_SETTINGS: {
  key: keyof Pick<
    CommunicationPreferences,
    "invoiceNotifications" | "paymentReceipts" | "appointmentReminders" | "matterUpdates" | "documentRequests"
  >;
  label: string;
  description: string;
}[] = [
  {
    key: "invoiceNotifications",
    label: "Invoice Notifications",
    description: "How clients receive invoice alerts",
  },
  {
    key: "paymentReceipts",
    label: "Payment Receipts",
    description: "How clients receive payment confirmations",
  },
  {
    key: "appointmentReminders",
    label: "Appointment Reminders",
    description: "How clients are reminded about upcoming appointments",
  },
  {
    key: "matterUpdates",
    label: "Matter Updates",
    description: "How clients receive case status updates",
  },
  {
    key: "documentRequests",
    label: "Document Requests",
    description: "How clients are notified about document requests",
  },
];

const CommunicationPreferencesModal: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<CommunicationPreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hasPhoneNumbers, setHasPhoneNumbers] = useState<boolean | null>(null);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [prefsRes, phoneRes] = await Promise.allSettled([
        firmApi.getCommunicationPreferences(),
        twilioApi.listNumbers({ limit: 1 }),
      ]);
      if (prefsRes.status === "fulfilled" && prefsRes.value.data?.communicationPreferences) {
        setPrefs({ ...DEFAULT_PREFS, ...prefsRes.value.data.communicationPreferences });
      }
      if (phoneRes.status === "fulfilled") {
        const numbers = phoneRes.value.data?.data || [];
        setHasPhoneNumbers(numbers.length > 0);
      } else {
        setHasPhoneNumbers(false);
      }
    } catch {
      // If no prefs exist yet, use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchPrefs();
  }, [open, fetchPrefs]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await firmApi.updateCommunicationPreferences(prefs);
      onSaved?.();
      onClose();
    } catch {
      setError("Failed to save preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const setChannel = (key: keyof CommunicationPreferences, value: ChannelOption) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Communication Preferences"
      subtitle="Configure firm-wide default notification settings"
      icon={Settings}
      size="lg"
      footer={
        <div className="flex items-center justify-between">
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-60"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => !saving && (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => !saving && (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Preferences
            </button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Info banner */}
          <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
            <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              These are your firm's default settings. You can override them for individual clients.
            </p>
          </div>

          {/* No phone number warning */}
          {hasPhoneNumbers === false &&
            (Object.entries(prefs).some(
              ([k, v]) => k !== "generalCommunications" && k !== "preferredChannel" && (v === "sms" || v === "both")
            ) || prefs.preferredChannel === "sms" || prefs.preferredChannel === "whatsapp") && (
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                You don't have a phone number set up.{" "}
                <button
                  onClick={() => { onClose(); navigate("/messages"); }}
                  className="font-semibold underline hover:text-amber-900 transition-colors"
                >
                  Add a phone number
                </button>
                {" "}in Messages to send SMS and WhatsApp notifications.
              </p>
            </div>
          )}

          {/* Client Notifications Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
              Client Notifications
            </h3>
            <div className="space-y-1">
              {NOTIFICATION_SETTINGS.map((setting, idx) => (
                <motion.div
                  key={setting.key}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="flex items-center justify-between py-3.5 px-4 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0 mr-4">
                    <p className="text-sm font-semibold text-slate-700">{setting.label}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{setting.description}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 flex-shrink-0">
                    {CHANNEL_OPTIONS.map((opt) => {
                      const active = prefs[setting.key] === opt.value;
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setChannel(setting.key, opt.value)}
                          className={`
                            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200
                            ${
                              active
                                ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-200"
                                : "text-slate-500 hover:text-slate-700"
                            }
                          `}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* General Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
              General
            </h3>

            {/* General Communications Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="flex items-center justify-between py-3.5 px-4 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <div className="min-w-0 mr-4">
                <p className="text-sm font-semibold text-slate-700">General Communications</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Allow sending newsletters and general updates
                </p>
              </div>
              <button
                onClick={() => setPrefs((p) => ({ ...p, generalCommunications: !p.generalCommunications }))}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 flex-shrink-0
                  ${prefs.generalCommunications ? "bg-emerald-600" : "bg-slate-300"}
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200
                    ${prefs.generalCommunications ? "translate-x-5" : "translate-x-0.5"}
                  `}
                />
              </button>
            </motion.div>

            {/* Preferred Channel */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.26 }}
              className="py-3.5 px-4 rounded-xl hover:bg-slate-50 transition-colors mt-1"
            >
              <div className="mb-3">
                <p className="text-sm font-semibold text-slate-700">Preferred Channel</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Default communication channel for new conversations
                </p>
              </div>
              <div className="flex items-center gap-2">
                {PREFERRED_CHANNELS.map((ch) => {
                  const active = prefs.preferredChannel === ch.value;
                  const Icon = ch.icon;
                  return (
                    <button
                      key={ch.value}
                      onClick={() => setPrefs((p) => ({ ...p, preferredChannel: ch.value }))}
                      className={`
                        flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border
                        ${
                          active
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm"
                            : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"
                        }
                      `}
                    >
                      <Icon className={`h-4 w-4 ${active ? "text-emerald-600" : ""}`} />
                      {ch.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </PremiumModal>
  );
};

export default CommunicationPreferencesModal;
